import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiEdit2, FiTrash2, FiDownload, FiPrinter } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { toast, confirmDelete, showError } from '../../utils/alert';
import PageHeader from '../../components/common/PageHeader';
import Loader from '../../components/common/Loader';
import StatusBadge from '../../components/common/StatusBadge';
import { formatDate, unwrapList } from '../../utils/format';
import { gatepassToPDF } from '../../utils/export';
import { COMPANY_NAME } from '../../config';
import { useAuth } from '../../context/AuthContext';

export default function GatepassDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [g, setG] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [gRes, sRes] = await Promise.all([
        api.gatepasses.get(id),
        api.settings.get(),
      ]);
      const gatepass = gRes.data.gatepass || gRes.data;
      if (gatepass && gatepass.party_id && !gatepass.party) {
        try {
          const pRes = await api.parties.list({ per_page: 500 });
          const list = unwrapList(pRes.data, 'parties');
          const found = list.find((p) => Number(p.id) === Number(gatepass.party_id));
          if (found) gatepass.party = found;
        } catch {
          // fallback lookup failed — keep as is
        }
      }
      setG(gatepass);
      const raw = sRes.data.settings || sRes.data || {};
      const flat = {};
      if (typeof raw === 'object' && !Array.isArray(raw)) {
        Object.values(raw).forEach((group) => {
          if (Array.isArray(group)) {
            group.forEach((s) => { flat[s.key] = s.value; });
          }
        });
      }
      setSettings(flat);
    } catch (err) {
      showError('Failed to load gatepass', extractError(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    const res = await confirmDelete(`gatepass ${g.gatepass_no}`);
    if (!res.isConfirmed) return;
    try {
      await api.gatepasses.remove(id);
      toast('success', '', 'Gatepass deleted');
      navigate('/gatepasses');
    } catch (err) {
      showError('Failed to delete gatepass', extractError(err));
    }
  };

  const handlePDF = async (copyOverride = null, print = false) => {
    try {
      await gatepassToPDF(g, COMPANY_NAME, settings, copyOverride, print);
      toast('success', '', print ? 'Print dialog opened' : 'PDF downloaded');
    } catch (err) {
      showError('Failed to generate PDF', extractError(err));
    }
  };

  if (loading) return <Loader text="Loading gatepass..." />;
  if (!g) return null;

  const items = g.items || [];
  const company = g.party?.name || g.other_party_name || '—';

  return (
    <div className="page">
      <PageHeader
        title={g.gatepass_no}
        subtitle={`${g.type === 'IN' ? 'Material In' : 'Material Out'} — ${formatDate(g.date)}`}
        crumbs={[{ label: 'Inventory', to: '/gatepasses' }, { label: 'Gatepass', to: '/gatepasses' }, { label: g.gatepass_no }]}
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary" onClick={() => navigate('/gatepasses')}><FiArrowLeft /> Back</button>
            <button className="btn btn-secondary" onClick={() => handlePDF()}><FiDownload /> Download PDF</button>
            <button className="btn btn-secondary" onClick={() => handlePDF(null, true)}><FiPrinter /> Print</button>
            <button className="btn btn-secondary" onClick={() => handlePDF('original', true)}><FiPrinter /> Original Print</button>
            <button className="btn btn-secondary" onClick={() => handlePDF('duplicate', true)}><FiPrinter /> Duplicate Print</button>
            {hasPermission('edit-gatepasses') && (
              <button className="btn btn-primary" onClick={() => navigate(`/gatepasses/${id}/edit`)}><FiEdit2 /> Edit</button>
            )}
            {hasPermission('delete-gatepasses') && (
              <button className="btn btn-danger-outline" onClick={handleDelete}><FiTrash2 /> Delete</button>
            )}
          </div>
        }
      />

      <div className="card">
        <div className="card-body">
          <div className="detail-grid">
            <div className="detail-item"><div className="d-label">Gatepass No</div><div className="d-value">{g.gatepass_no}</div></div>
            <div className="detail-item"><div className="d-label">Type</div><div className="d-value"><StatusBadge status={g.type === 'IN' ? 'Material In' : 'Material Out'} /></div></div>
            <div className="detail-item"><div className="d-label">Copy</div><div className="d-value">{g.copy_type === 'duplicate' ? 'Duplicate' : 'Original'}</div></div>
            <div className="detail-item"><div className="d-label">Date</div><div className="d-value">{formatDate(g.date)}</div></div>
            <div className="detail-item"><div className="d-label">Company</div><div className="d-value">{company}</div></div>
            <div className="detail-item"><div className="d-label">Person Name</div><div className="d-value">{g.person_name || '—'}</div></div>
            <div className="detail-item"><div className="d-label">Vehicle No</div><div className="d-value">{g.vehicle_no || '—'}</div></div>
            <div className="detail-item"><div className="d-label">Receiver Name</div><div className="d-value">{g.receiver_name || '—'}</div></div>
            <div className="detail-item"><div className="d-label">Transporter</div><div className="d-value">{g.transporter || '—'}</div></div>
            <div className="detail-item"><div className="d-label">Returnable</div><div className="d-value"><StatusBadge status={g.is_returnable ? 'Returnable' : 'Non-Returnable'} /></div></div>
            <div className="detail-item" style={{ gridColumn: '1 / -1' }}><div className="d-label">Notes</div><div className="d-value">{g.notes || '—'}</div></div>
            <div className="detail-item"><div className="d-label">Created By</div><div className="d-value">{g.creator?.name || '—'}</div></div>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 18 }}>
        <div className="card-header"><div className="card-title">Articles ({items.length})</div></div>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Article No</th>
                <th>Name</th>
                <th>Unit</th>
                <th style={{ textAlign: 'right' }}>Quantity</th>
                <th>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => (
                <tr key={item.id || i}>
                  <td><b>{item.article_no || '—'}</b></td>
                  <td>{item.name || '—'}</td>
                  <td>{item.unit || '—'}</td>
                  <td style={{ textAlign: 'right' }}>{Number(item.quantity)}</td>
                  <td>{item.remarks || '—'}</td>
                </tr>
              ))}
              {!items.length && (
                <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No articles</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
